import { ref, readonly } from 'vue'
import Keycloak from 'keycloak-js'
import type { IKeycloakState } from '../types/keycloak-types'

const keycloakInstance = ref<Keycloak | null>(null)
const state = ref<IKeycloakState>({
  isInitialized: false,
  isAuthenticated: false,
  token: null,
  refreshToken: null,
  userId: null,
  userName: null,
  userDisplayName: null,
  tenantId: null,
})

const MIN_TOKEN_VALIDITY_SECONDS = 30
let refreshInFlight: Promise<string | null> | null = null

function resetState(tenantId: string | null = null) {
  state.value = {
    isInitialized: false,
    isAuthenticated: false,
    token: null,
    refreshToken: null,
    userId: null,
    userName: null,
    userDisplayName: null,
    tenantId,
  }
  keycloakInstance.value = null
  refreshInFlight = null
}

export function useKeycloak() {
  /**
   * Initialize Keycloak with dynamic realm (tenantId)
   * Uses environment variables for URL and clientId
   */
  async function initialize(tenantId: string): Promise<boolean> {
    // Reset if initialized with a different realm to avoid cross-tenant bleed
    if (state.value.isInitialized && state.value.tenantId !== tenantId) {
      resetState(tenantId)
    }

    // Skip if already initialized with same realm
    if (
      state.value.isInitialized &&
      state.value.tenantId === tenantId &&
      keycloakInstance.value
    ) {
      return state.value.isAuthenticated
    }

    // Validate environment variables
    const keycloakUrl = import.meta.env.VITE_KEYCLOAK_URL
    const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID

    if (!keycloakUrl || !clientId) {
      console.error(
        'Keycloak configuration missing: VITE_KEYCLOAK_URL and VITE_KEYCLOAK_CLIENT_ID are required'
      )
      resetState(tenantId)
      state.value.isInitialized = true
      return false
    }

    const keycloak = new Keycloak({
      url: keycloakUrl,
      realm: tenantId,
      clientId: clientId,
    })

    try {
      // Detect Safari/iOS which may block silent SSO due to ITP
      const isSafari =
        /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
        /iPad|iPhone|iPod/.test(navigator.userAgent)

      const authenticated = await keycloak.init({
        onLoad: 'check-sso',
        // Skip silent SSO on Safari as it's blocked by ITP
        silentCheckSsoRedirectUri: isSafari
          ? undefined
          : `${window.location.origin}/silent-check-sso.html`,
        pkceMethod: 'S256',
        // Disabled — Keycloak's iframe CORS check rejects wildcard subdomains.
        // silent-check-sso.html handles session checks instead.
        checkLoginIframe: false,
      })

      keycloakInstance.value = keycloak
      const tokenParsed = keycloak.tokenParsed as
        | Record<string, string>
        | undefined
      state.value = {
        isInitialized: true,
        isAuthenticated: authenticated,
        token: keycloak.token ?? null,
        refreshToken: keycloak.refreshToken ?? null,
        userId: keycloak.tokenParsed?.sub ?? null,
        userName:
          tokenParsed?.preferred_username ??
          tokenParsed?.email ??
          tokenParsed?.name ??
          null,
        userDisplayName:
          tokenParsed?.name ?? tokenParsed?.preferred_username ?? null,
        tenantId,
      }

      // Setup automatic token refresh
      keycloak.onTokenExpired = () => {
        refreshToken()
      }

      return authenticated
    } catch (error) {
      console.error('Keycloak initialization failed:', error)
      resetState(tenantId)
      state.value.isInitialized = true
      return false
    }
  }

  async function login(): Promise<void> {
    if (!keycloakInstance.value && state.value.tenantId) {
      await initialize(state.value.tenantId)
    }

    await keycloakInstance.value?.login({
      redirectUri: window.location.href,
    })
  }

  async function logout(): Promise<void> {
    await keycloakInstance.value?.logout({
      redirectUri: window.location.origin,
    })
  }

  async function refreshToken(): Promise<string | null> {
    if (!keycloakInstance.value) {
      return null
    }

    if (refreshInFlight) {
      return refreshInFlight
    }

    refreshInFlight = (async () => {
      try {
        const refreshed = await keycloakInstance.value?.updateToken(
          MIN_TOKEN_VALIDITY_SECONDS
        )
        if (refreshed) {
          state.value.token = keycloakInstance.value?.token ?? null
          state.value.refreshToken =
            keycloakInstance.value?.refreshToken ?? null
        }
        return state.value.token
      } catch {
        state.value.isAuthenticated = false
        state.value.token = null
        state.value.refreshToken = null
        return null
      } finally {
        refreshInFlight = null
      }
    })()

    return refreshInFlight
  }

  /**
   * Returns an auth callback compatible with apiClient.setAuthCallback()
   */
  function getAuthCallback() {
    return async () => {
      if (!state.value.isInitialized) {
        return { token: '', tenantId: '' }
      }

      await refreshToken()
      return {
        token: state.value.token ?? '',
        tenantId: state.value.tenantId ?? '',
        userId: state.value.userId ?? undefined,
      }
    }
  }

  function reset(tenantId: string | null = null) {
    resetState(tenantId)
  }

  return {
    state: readonly(state),
    initialize,
    login,
    logout,
    refreshToken,
    getAuthCallback,
    reset,
  }
}
